import React, { Component } from 'react';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import {
  INPUT_TYPES,
  typeValidators,
  typeErrorMessages,
  typePlaceholders,
  typeHeaders
} from '../utils';
import './Input.style.scss';

class Input extends Component {
  constructor(props) {
    super(props);
    this.state = {
      validationClass: '',
      // eslint-disable-next-line react/destructuring-assignment
      value: this.props.value
    };
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;
    if (value !== prevProps.value && value === '') {
      this.clearText();
    }
  }

  clearText = () => {
    this.setState({
      value: '',
      validationClass: ''
    });
  }

  onInputTextChange = (event) => {
    const inputText = event.target.value;
    const {
      inputType,
      onChange,
      validation,
      onValidate,
      onValidationError
    } = this.props;

    if (onChange) {
      onChange(inputText);
    } else {
      this.setState({ value: inputText });
    }

    let inputValidator = null;
    if (typeof validation === 'function') {
      inputValidator = validation;
    } else if (INPUT_TYPES.includes(inputType)) {
      inputValidator = typeValidators[inputType];
    } // else no prop or default validator fn

    let inputClass = '';
    if (typeof inputValidator === 'function') {
      if (inputValidator(inputText)) {
        inputClass = 'form-input--success';
        if (onValidate) onValidate();
      } else {
        if (inputText) {
          inputClass = 'form-input--error';
        }
        if (onValidationError) onValidationError();
      }
    }

    this.setState({
      validationClass: inputClass
    });
  };

  onInputTextFocus = (event) => {
    const { onFocus, scrollOnFocus } = this.props;
    if (onFocus) {
      onFocus(event.target);
    }
    if (scrollOnFocus) {
      event.target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
      event.target.focus();
    }
  };

  render() {
    const {
      autoFocus,
      className,
      cols,
      defaultValue,
      disabled,
      errorText,
      header,
      inputType,
      maxLength,
      name,
      onChange,
      onBlur,
      onKeyPress,
      pattern,
      placeholder,
      rows,
      type,
      mask,
      value: propValue
    } = this.props;

    const {
      validationClass,
      value: stateValue
    } = this.state;

    let errorMessage = null;
    if (typeof propErrorText === 'string') {
      errorMessage = errorText;
    } else if (INPUT_TYPES.includes(inputType)) {
      errorMessage = typeErrorMessages[inputType];
    } // else no prop or default errorText

    let placeholderMessage = null;
    if (typeof placeholder === 'string') {
      placeholderMessage = placeholder;
    } else if (INPUT_TYPES.includes(inputType)) {
      placeholderMessage = typePlaceholders[inputType];
    } // else no prop or default placeholder text

    let headerMessage = null;
    if (typeof header === 'string') {
      headerMessage = header;
    } else if (INPUT_TYPES.includes(inputType)) {
      headerMessage = typeHeaders[inputType];
    } // else no prop or default header

    const additionalProps = {};
    if (maxLength) additionalProps.maxLength = maxLength;
    if (onBlur) additionalProps.onBlur = onBlur;
    if (pattern) additionalProps.pattern = pattern;
    if (disabled) additionalProps.disabled = 'disabled';

    const value = onChange ? propValue : stateValue;
    if (typeof defaultValue !== 'undefined') {
      additionalProps.defaultValue = defaultValue;
    } else if (typeof value !== 'undefined') {
      additionalProps.value = value;
    }

    if (type === 'textarea') {
      additionalProps.rows = rows;
      additionalProps.cols = cols;
    }

    const Tag = type === 'textarea' ? 'textarea' : 'input';

    const formattedInput = () => (
      <Tag
        onKeyPress={onKeyPress}
        onChange={this.onInputTextChange}
        onFocus={this.onInputTextFocus}
        {...additionalProps} // eslint-disable-line
        className={`${className} form-input__field`}
        name={name}
        placeholder={placeholderMessage}
        type={type}
        autoFocus={autoFocus}
        mask={mask}
      />
    );

    const applyMask = () => (
      <InputMask
        mask={mask}
        maskChar={null}
        onChange={this.onInputTextChange}
        onFocus={this.onInputTextFocus}
        value={additionalProps.value}
      >
        {formattedInput}
      </InputMask>
    );

    return (
      <span className={'form-input ' + validationClass}>
        <span className="form-input__label-content">{headerMessage}</span>
        {mask ? applyMask() : formattedInput()}
        <span className="form-input-error__message">
          {validationClass === 'form-input--error' ? errorMessage : ''}
        </span>
      </span>
    );
  }
}

Input.propTypes = {
  /** Additional classes to add */
  className: PropTypes.string,
  /** Error text displayed below input box */
  errorText: PropTypes.string,
  /** Header text displayed above input box */
  header: PropTypes.string,
  /** disables the input field */
  disabled: PropTypes.bool,
  /** Placeholder text for input box */
  placeholder: PropTypes.string,
  /** Text value for input box */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** React defaultValue prop. Sets the initial value only */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** maxlength for input box */
  maxLength: PropTypes.string,
  /** html name element */
  name: PropTypes.string,
  /** Validation function to determine success or error */
  validation: PropTypes.func,
  /** onChange function to return the input value */
  onChange: PropTypes.func,
  /** onFocus function to have focus set into the input */
  onFocus: PropTypes.func,
  /** onBlur function to have blur set into the input */
  onBlur: PropTypes.func,
  /**
   * onKeyPress function to have keyPress set into the input
   * WARNING: use onKeyPress only if onChange prop is not triggered for your keyPress requirement.
   * */
  onKeyPress: PropTypes.func,
  /** onValidate function to be called when validation is successful */
  onValidate: PropTypes.func,
  /** onValidate function to be called when validation fails */
  onValidationError: PropTypes.func,
  pattern: PropTypes.string,
  type: PropTypes.oneOf(['text', 'textarea']),
  rows: PropTypes.string,
  cols: PropTypes.string,
  mask: PropTypes.string,
  autoFocus: PropTypes.bool,
  scrollOnFocus: PropTypes.bool,
  inputType: PropTypes.oneOf(INPUT_TYPES)
};

Input.defaultProps = {
  className: null,
  errorText: null,
  header: null,
  disabled: false,
  placeholder: null,
  value: '',
  defaultValue: undefined,
  maxLength: null,
  name: '',
  validation: null,
  onBlur: null,
  onChange: null,
  onFocus: null,
  onKeyPress: null,
  onValidate: null,
  onValidationError: null,
  pattern: null,
  type: 'text',
  rows: '0',
  cols: '0',
  mask: undefined,
  autoFocus: false,
  scrollOnFocus: false,
  inputType: null
};

export default Input;
